import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { App, Button, Card, Col, Input, InputNumber, Row, Select, Space, Spin, Typography, Upload } from 'antd';
import { CopyOutlined, ExportOutlined, FileImageOutlined, FontSizeOutlined, PictureOutlined, SaveOutlined, UploadOutlined } from '@ant-design/icons';
import { ProgramRefPicker } from '@/pages/Studio/ProgramRefPicker';
import { useTopics, useUpdateTopic } from '@/hooks/useTopics';
import { useLanguages } from '@/hooks/useLanguages';
import { useFlyerDoc, useCreateFlyerDoc, useUpdateFlyerDoc } from '@/hooks/useFlyerDocs';
import { getFlyerDoc, uploadDocAttachment } from '@/api/flyerDocs';
import { getTopic } from '@/api/topics';
import type { FlyerDocElement, FlyerDocumentInput } from '@/types/flyerDoc';
import type { PctRect } from '@/lib/flyer-canvas';
import { moveRect, round3 } from '@/lib/flyer-canvas';
import { bumpZ, nextZ, upsertFlyerLink } from '@/lib/flyer-compose';
import { ComposerCanvas } from './ComposerCanvas';
import { ElementInspector } from './ElementInspector';
import { ImagePickerModal } from './ImagePickerModal';
import { PrototypeImportModal } from './PrototypeImportModal';
import { exportDocPng } from './exportDoc';

const SIZE_PRESETS = [
  { label: 'Letter (2550×3300)', w: 2550, h: 3300 },
  { label: 'Story (1080×1920)', w: 1080, h: 1920 },
  { label: 'Square (1080×1080)', w: 1080, h: 1080 },
];

const newText = (z: number): FlyerDocElement => ({
  id: crypto.randomUUID().toUpperCase(), kind: 'text',
  x_percent: 0.25, y_percent: 0.45, width_percent: 0.5, height_percent: 0.08, z,
  text: 'Text', style: { color: '#000000', fontSize: 48, alignment: 'center', fontWeight: 'regular', isItalic: false },
});
const newImage = (z: number, url: string): FlyerDocElement => ({
  id: crypto.randomUUID().toUpperCase(), kind: 'image',
  x_percent: 0.35, y_percent: 0.35, width_percent: 0.3, height_percent: 0.2, z, src_url: url,
});

export function ComposerPage() {
  const { message } = App.useApp();
  const nav = useNavigate();
  const { id: routeId } = useParams();
  const isNew = routeId === 'new';
  const docId = isNew ? null : routeId ?? null;

  const docQ = useFlyerDoc(docId);
  const langsQ = useLanguages();
  const topicsQ = useTopics({ status: 'active' });
  const create = useCreateFlyerDoc();
  const update = useUpdateFlyerDoc();
  const updateTopic = useUpdateTopic();

  const [name, setName] = useState('');
  const [lang, setLang] = useState('en');
  const [size, setSize] = useState({ width: 1080, height: 1920 });
  const [sourceTopicNo, setSourceTopicNo] = useState<string | null>(null);
  const [elements, setElements] = useState<FlyerDocElement[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [pickerFor, setPickerFor] = useState<'new' | string | null>(null); // 'new' = add element; else element id to replace
  const [dirty, setDirty] = useState(isNew);
  const [hydrated, setHydrated] = useState(isNew);
  const [busy, setBusy] = useState(false);
  const [protoOpen, setProtoOpen] = useState(false);

  useEffect(() => {
    const d = docQ.data;
    if (!d || hydrated) return;
    setName(d.name); setLang(d.lang);
    setSize({ width: d.width, height: d.height });
    setSourceTopicNo(d.source_topic_no);
    setElements(d.doc_json);
    setHydrated(true);
  }, [docQ.data, hydrated]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const selected = useMemo(() => elements.find((e) => e.id === selectedId) ?? null, [elements, selectedId]);
  const mutate = (fn: (prev: FlyerDocElement[]) => FlyerDocElement[]) => { setElements(fn); setDirty(true); };
  const boundTopicId = useMemo(
    () => (topicsQ.data ?? []).find((t) => String(t.topic_no ?? t.id) === sourceTopicNo)?.id ?? null,
    [topicsQ.data, sourceTopicNo],
  );

  // Arrows nudge the selected element (Shift = ×10), Esc deselects; skipped while typing.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;
      if (e.key === 'Escape') { setSelectedId(null); setEditingId(null); return; }
      if (!selectedId || !e.key.startsWith('Arrow')) return;
      e.preventDefault();
      const step = e.shiftKey ? 0.05 : 0.005;
      const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
      const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
      mutate((prev) => prev.map((el) => {
        if (el.id !== selectedId) return el;
        const r = moveRect({ x: el.x_percent, y: el.y_percent, w: el.width_percent, h: el.height_percent }, dx, dy);
        return { ...el, x_percent: round3(r.x), y_percent: round3(r.y), width_percent: round3(r.w), height_percent: round3(r.h) };
      }));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const onChangeRect = (id: string, r: PctRect) =>
    mutate((prev) => prev.map((e) => (e.id === id
      ? { ...e, x_percent: round3(r.x), y_percent: round3(r.y), width_percent: round3(r.w), height_percent: round3(r.h) }
      : e)));

  const body = (): FlyerDocumentInput => ({
    name: name.trim(), lang, width: size.width, height: size.height,
    doc_json: elements, source_topic_no: sourceTopicNo,
  });

  const save = async (): Promise<string | null> => {
    if (!name.trim()) { message.warning('Enter a document name'); return null; }
    try {
      if (docId) {
        await update.mutateAsync({ id: docId, b: body() });
        setDirty(false);
        return docId;
      }
      const row = await create.mutateAsync(body());
      setDirty(false);
      nav(`/flyers/compose/${row.id}`, { replace: true });
      return row.id;
    } catch (e) { message.error((e as Error).message); return null; }
  };

  const exportAndSaveBack = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const id = await save();
      if (!id) return;
      const fresh = await getFlyerDoc(id);
      const blob = await exportDocPng({ width: size.width, height: size.height, doc_json: elements, background_url: fresh.background_url ?? null });
      const up = await uploadDocAttachment(id, blob, 'export', `${name.trim() || 'flyer'}-${lang}.png`);
      const prevUrl = fresh.exports_json?.[lang]?.url ?? null;
      await update.mutateAsync({ id, b: { exports_json: { ...(fresh.exports_json ?? {}), [lang]: { attachment_id: up.id, url: up.url } } } });
      if (sourceTopicNo) {
        const topic = (topicsQ.data ?? []).find((t) => String(t.topic_no ?? t.id) === sourceTopicNo);
        if (!topic) { message.warning('Exported, but the bound program was not found — link not written.'); return; }
        const freshTopic = await getTopic(topic.id);
        const details = upsertFlyerLink(freshTopic.details ?? {}, { lang, name: name.trim(), url: up.url, prevUrl });
        await updateTopic.mutateAsync({ id: topic.id, input: { details } });
        message.success(`Exported and saved to program ${sourceTopicNo} as ${lang} flyer`);
      } else {
        message.success('Exported (no program bound — link not written)');
      }
    } catch (e) { message.error((e as Error).message); }
    finally { setBusy(false); }
  };

  if (!isNew && docQ.isLoading) return <Spin style={{ marginTop: 80 }} />;
  if (!isNew && docQ.isError) return <Typography.Text type="danger">{(docQ.error as Error).message}</Typography.Text>;

  return (
    <div style={{ maxWidth: 1280 }}>
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }} wrap>
        <Space wrap>
          <Input placeholder="Document name" value={name} onChange={(e) => { setName(e.target.value); setDirty(true); }} style={{ width: 200 }} />
          <Select style={{ width: 140 }} value={lang} loading={langsQ.isLoading}
            options={(langsQ.data ?? []).map((l) => ({ value: l.code, label: l.code }))}
            onChange={(v) => { setLang(v); setDirty(true); }} />
          {isNew && (
            <Select placeholder="Size preset" style={{ width: 180 }}
              options={SIZE_PRESETS.map((p, i) => ({ value: i, label: p.label }))}
              onChange={(i) => { setSize({ width: SIZE_PRESETS[i].w, height: SIZE_PRESETS[i].h }); setDirty(true); }} />
          )}
          <Space size={4}>
            <InputNumber min={100} value={size.width} disabled={!isNew} onChange={(v) => { setSize((s) => ({ ...s, width: v ?? s.width })); setDirty(true); }} style={{ width: 90 }} />
            ×
            <InputNumber min={100} value={size.height} disabled={!isNew} onChange={(v) => { setSize((s) => ({ ...s, height: v ?? s.height })); setDirty(true); }} style={{ width: 90 }} />
          </Space>
        </Space>
        <Space>
          <Button icon={<FontSizeOutlined />} onClick={() => { const el = newText(nextZ(elements)); mutate((p) => [...p, el]); setSelectedId(el.id); }}>Text</Button>
          <Button icon={<PictureOutlined />} onClick={() => setPickerFor('new')}>Image</Button>
          <Button icon={<CopyOutlined />} disabled={!selected} onClick={() => {
            if (!selected) return;
            const el = { ...selected, id: crypto.randomUUID().toUpperCase(), z: nextZ(elements), y_percent: Math.min(0.9, selected.y_percent + 0.05) };
            mutate((p) => [...p, el]); setSelectedId(el.id);
          }}>Duplicate</Button>
          <Button danger disabled={!selected} onClick={() => { if (selected) { mutate((p) => p.filter((e) => e.id !== selected.id)); setSelectedId(null); } }}>Delete</Button>
          <Button icon={<SaveOutlined />} onClick={save} loading={create.isPending || update.isPending}>{dirty ? 'Save*' : 'Save'}</Button>
          <Button type="primary" icon={<ExportOutlined />} onClick={exportAndSaveBack} loading={busy}>Export & save to program</Button>
        </Space>
      </Space>
      <Row gutter={12}>
        <Col xs={24} md={16}>
          <ComposerCanvas width={size.width} height={size.height}
            backgroundUrl={docQ.data?.background_url ?? null}
            elements={elements} selectedId={selectedId} editingId={editingId}
            onSelect={(id) => { setSelectedId(id); if (editingId && id !== editingId) setEditingId(null); }}
            onChangeRect={onChangeRect}
            onStartEdit={setEditingId}
            onCommitText={(id, text) => { mutate((p) => p.map((e) => (e.id === id ? { ...e, text } : e))); setEditingId(null); }} />
        </Col>
        <Col xs={24} md={8}>
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <Card size="small" title="Program">
              <ProgramRefPicker initialTopicNo={sourceTopicNo ?? undefined}
                onPick={(p) => { setSourceTopicNo(p.topicNo); setDirty(true); }}
                onClear={() => { setSourceTopicNo(null); setDirty(true); }} />
              {sourceTopicNo && <Typography.Text type="secondary" style={{ fontSize: 12 }}>bound: {sourceTopicNo}</Typography.Text>}
            </Card>
            <Card size="small" title="Background">
              <Space direction="vertical" style={{ width: '100%' }} size={8}>
                {!docId && <Typography.Text type="warning" style={{ fontSize: 12 }}>Save first to set a background.</Typography.Text>}
                <Upload showUploadList={false} accept="image/*" disabled={!docId}
                  customRequest={async ({ file, onSuccess, onError }) => {
                    try {
                      await uploadDocAttachment(docId!, file as File, 'background');
                      onSuccess?.({}); message.success('Background updated');
                      docQ.refetch();
                    } catch (e) { onError?.(e as Error); message.error((e as Error).message); }
                  }}>
                  <Button size="small" icon={<UploadOutlined />} disabled={!docId}>Upload</Button>
                </Upload>
                <Button size="small" icon={<FileImageOutlined />} disabled={!docId || !sourceTopicNo}
                  onClick={() => setProtoOpen(true)}>
                  Import from program
                </Button>
              </Space>
            </Card>
            <Card size="small" title="Element">
              <ElementInspector element={selected}
                onChange={(el) => mutate((p) => p.map((e) => (e.id === el.id ? el : e)))}
                onBump={(id, dir) => mutate((p) => bumpZ(p, id, dir))}
                onReplaceImage={(id) => setPickerFor(id)} />
            </Card>
          </Space>
        </Col>
      </Row>
      <ImagePickerModal open={pickerFor !== null} docId={docId}
        onClose={() => setPickerFor(null)}
        onPick={(url) => {
          if (pickerFor === 'new') { const el = newImage(nextZ(elements), url); mutate((p) => [...p, el]); setSelectedId(el.id); }
          else if (pickerFor) mutate((p) => p.map((e) => (e.id === pickerFor ? { ...e, src_url: url } : e)));
        }} />
      <PrototypeImportModal open={protoOpen} docId={docId} topicId={boundTopicId}
        onClose={() => setProtoOpen(false)}
        onImported={(doc) => { setSize({ width: doc.width, height: doc.height }); docQ.refetch(); }} />
    </div>
  );
}
